import React, { useState, useRef, useEffect } from 'react';
import { LogOut, Menu, User, Moon, Sun } from 'lucide-react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import AlertCenter from '../alerts/AlertCenter';

interface HeaderProps {
  toggleSidebar: () => void;
}

export default function Header({ toggleSidebar }: HeaderProps) {
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { theme, toggleTheme } = useTheme();

  // Get user data from localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user');
      return userData ? JSON.parse(userData) : null;
    } catch (e) {
      return null;
    }
  };

  const userData = getUserData();
  const userName = userData?.name || 'Admin User';
  const userRole = userData?.role || 'Super Admin';

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    if (isLoggingOut) return;

    setIsLoggingOut(true);

    // For now, skip the API call and just do a local logout since we're getting 401s
    // This is a pragmatic solution to ensure users can log out reliably
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');

    // Use window.location.href for a cleaner page reload and navigation
    window.location.href = '/login';
  };

  return (
    <header className="h-14 border-b flex items-center justify-between px-4 md:px-6 bg-card">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="flex lg:hidden text-primary"
          onClick={toggleSidebar}
          aria-label="Toggle sidebar menu"
        >
          <Menu className="h-6 w-6" />
        </Button>
        <h1 className="text-lg font-semibold">BuildSupplyHub</h1>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Theme Toggle Button */}
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>

        {/* Alert Center - Real-time notifications */}
        <AlertCenter />

        {/* User Menu */}
        <div className="relative" ref={userMenuRef}>
          <div
            className="flex items-center gap-2 pl-3 cursor-pointer"
            onClick={() => setUserMenuOpen(!userMenuOpen)}
          >
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>
            <div className="hidden sm:block text-sm">
              <p className="font-medium">{userName}</p>
              <p className="text-xs text-muted-foreground">{userRole}</p>
            </div>
          </div>

          {userMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-card rounded-md shadow-lg border z-50">
              <div className="py-1">
                <button
                  className="flex w-full items-center px-4 py-2 text-sm hover:bg-accent text-destructive"
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  {isLoggingOut ? 'Logging out...' : 'Logout'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}